import React from 'react';

const containerStyle: React.CSSProperties = {
  maxWidth: '900px',
  margin: '60px auto',
  padding: '40px',
  fontFamily: 'Segoe UI, Arial, sans-serif',
  color: '#2c3e50',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  lineHeight: 1.75,
};

const titleStyle: React.CSSProperties = {
  fontSize: '32px',
  textAlign: 'center',
  color: '#1a237e',
  marginBottom: '12px',
};

const dateStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '14px',
  color: '#616161',
  marginBottom: '40px',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '32px',
};

const headingStyle: React.CSSProperties = {
  fontSize: '22px',
  color: '#0d47a1',
  marginBottom: '12px',
  borderBottom: '1px solid #ccc',
  paddingBottom: '4px',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '20px',
  marginTop: '12px',
};

type SectionProps = {
  title: string;
  children: React.ReactNode;
  style: React.CSSProperties;
  headingStyle: React.CSSProperties;
};

const Section: React.FC<SectionProps> = ({
  title,
  children,
  style,
  headingStyle,
}) => (
  <section style={style}>
    <h2 style={headingStyle}>{title}</h2>
    {children}
  </section>
);

const TermsOfUse: React.FC = () => {
  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>WeCheck Terms of Use</h1>
      <p style={dateStyle}>
        <strong>Last Updated:</strong> June 2025
      </p>

      <p>
        Welcome to the WeCheck Terms of Use ("Terms"). These Terms govern your
        access to and use of the WeCheck web platform at the platform
        ("Platform"), mobile applications ("Apps"), and related services
        (collectively, "Services"). By using our Services, you agree to these
        Terms. If you do not agree, do not use the Services.
      </p>

      <Section
        title="1. Acceptance of Terms"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>
          1.1. By registering an account or using WeCheck’s Services, you
          confirm that you have read, understood, and agreed to these Terms, our
          Privacy Policy, and any additional policies referenced herein.
        </p>
        <p>
          1.2. If you are using the Services on behalf of an organization, you
          represent that you have the authority to bind that entity to these
          Terms.
        </p>
      </Section>

      <Section
        title="2. Account Registration"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>
          2.1. To access certain features, you must register an account by
          providing accurate information (e.g., name, email, phone number).
        </p>
        <p>
          2.2. You are responsible for maintaining the confidentiality of your
          login credentials and for all activities under your account.
        </p>
        <p>
          2.3. WeCheck reserves the right to suspend or terminate accounts that
          violate these Terms or applicable laws.
        </p>
      </Section>

      <Section
        title="3. Permitted Use"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>
          3.1. You may use the Services solely for their intended purpose (e.g.,
          team management, document validation, workflow automation) and in
          compliance with all applicable laws.
        </p>
      </Section>

      <Section
        title="4. Prohibited Conduct"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>You agree not to:</p>
        <ul style={listStyle}>
          <li>
            Violate any third-party rights (e.g., intellectual property or
            privacy).
          </li>
          <li>Upload illegal, fraudulent, or harmful content.</li>
          <li>
            Attempt unauthorized access to data or interfere with the Services’
            security.
          </li>
          <li>
            Use automated systems (e.g., bots, scrapers) without prior written
            consent.
          </li>
        </ul>
      </Section>

      <Section
        title="5. Intellectual Property"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>
          5.1. All rights to the Services (software, trademarks, content) are
          owned by WeCheck or its licensors.
        </p>
        <p>
          5.2. You retain rights to materials you submit but grant WeCheck a
          non-exclusive license to process them for the Services.
        </p>
      </Section>

      <Section
        title="6. Privacy & Data Protection"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>
          6.1. Personal data is processed as described in our Privacy Policy,
          compliant with GDPR, LGPD, and other applicable laws.
        </p>
        <p>6.2. By using the Services, you consent to such processing.</p>
      </Section>

      <Section
        title="7. Disclaimers & Limitations of Liability"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>
          7.1. WeCheck provides the Services "as is" and does not guarantee:
        </p>
        <ul style={listStyle}>
          <li>Uninterrupted or error-free operation.</li>
          <li>Accuracy of third-party content.</li>
        </ul>
        <p>
          7.2. To the fullest extent permitted by law, WeCheck is not liable
          for:
        </p>
        <ul style={listStyle}>
          <li>Indirect, incidental, or consequential damages.</li>
          <li>Loss of data or profits.</li>
        </ul>
      </Section>

      <Section
        title="8. Modifications & Termination"
        style={sectionStyle}
        headingStyle={headingStyle}
      >
        <p>
          8.1. WeCheck may update these Terms at any time. Changes will be
          posted on the Site with an updated "Last Updated" date.
        </p>
        <p>
          8.2. You may terminate your account at any time. WeCheck may suspend
          or terminate access for violations of these Terms.
        </p>
      </Section>

      <Section
        title="9. General Provisions"
        style={{...sectionStyle, paddingBottom: '40px'}}
        headingStyle={headingStyle}
      >
        <p>
          9.1. Governing Law: These Terms are governed by Brazilian law, with
          jurisdiction in São Paulo, SP.
        </p>
        <p>
          9.2. Severability: If any provision is deemed invalid, the remainder
          of the Terms remains enforceable.
        </p>
        <p>
          9.3. Contact: For questions, contact our Data Protection Officer
          (DPO):
        </p>
        <ul style={listStyle}>
          <li>
            <strong>Name:</strong> Bruno Barros
          </li>
          <li>
            <strong>Email:</strong> contato@wecheck.com.br
          </li>
        </ul>
      </Section>
    </div>
  );
};

export default TermsOfUse;
